import { formatDateIn } from '../../utils/dateFormats';
import type { TranslateFn } from '../../types/i18n';
import { DEFAULT_LANGUAGE } from '../../i18n';
import React from 'react';
import { Notification } from '../../types/notifications';

/**
 * Gedeelde presentatie van meldingen, gebruikt door zowel de desktop-dropdown
 * (NotificationItem) als het mobiele Meer-paneel. Staat los zodat beide dezelfde
 * iconen en tijdnotatie tonen zonder de logica te dupliceren.
 */

const iconStyles: Record<string, { wrapper: string; path: string }> = {
  match_completed: {
    wrapper: 'bg-green-100 text-green-600',
    path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  match_upcoming: {
    wrapper: 'bg-blue-100 text-blue-600',
    path: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  player_update: {
    wrapper: 'bg-purple-100 text-purple-600',
    path: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  },
  lineup_change: {
    wrapper: 'bg-orange-100 text-orange-600',
    path: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  analysis_ready: {
    wrapper: 'bg-indigo-100 text-indigo-600',
    path: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
};

const fallbackIcon = {
  wrapper: 'bg-gray-100 text-gray-600',
  path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

interface NotificationTypeIconProps {
  type: Notification['type'];
}

export const NotificationTypeIcon: React.FC<NotificationTypeIconProps> = ({ type }) => {
  const { wrapper, path } = iconStyles[type] ?? fallbackIcon;

  return (
    <div className={`rounded-full p-2 shrink-0 ${wrapper}`} aria-hidden="true">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path} />
      </svg>
    </div>
  );
};

/**
 * Relatieve tijd, met een datum zodra het langer dan een week geleden is.
 *
 * `t` hangt aan de `notifications`-namespace en is optioneel, zoals bij de helpers in
 * matchUtils: zonder `t` blijft de Nederlandse tekst staan.
 */
export const formatTimeAgo = (
  createdAt: string,
  language: string = DEFAULT_LANGUAGE,
  t?: TranslateFn,
): string => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / (1000 * 60));

  const label = (key: string, fallback: string, value?: number) =>
    t ? t(`timeAgo.${key}`, { value } as never) : fallback;

  if (diffInMinutes < 1) return label('now', 'Nu');
  if (diffInMinutes < 60) return label('minutes', `${diffInMinutes}m geleden`, diffInMinutes);

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return label('hours', `${diffInHours}u geleden`, diffInHours);

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return label('days', `${diffInDays}d geleden`, diffInDays);

  return formatDateIn(language, created, 'dayMonth');
};
